/**
 * FSS Mask Manager
 *
 * Manages CSS mask calculations for multiple Full Screen Scroll (FSS) ads
 * within the same feed element. Ensures that multiple FSS ads can coexist
 * by generating a single linear gradient mask with multiple "holes".
 */

interface FSSPosition {
  top: number;
  left: number;
  right: number;
  bottom: number;
  id: string; // Unique identifier for this FSS ad
}

class FSSMaskManager {
  private static instance: FSSMaskManager;
  // Map of feed elements to their FSS positions
  private fssPositions: WeakMap<HTMLElement, Map<string, FSSPosition>>;

  private constructor() {
    this.fssPositions = new WeakMap();
  }

  static getInstance(): FSSMaskManager {
    if (!FSSMaskManager.instance) {
      FSSMaskManager.instance = new FSSMaskManager();
    }
    return FSSMaskManager.instance;
  }

  registerFSS(feedElement: HTMLElement, position: FSSPosition): void {
    let positions = this.fssPositions.get(feedElement);

    if (!positions) {
      positions = new Map();
      this.fssPositions.set(feedElement, positions);
    }

    positions.set(position.id, position);
    this.updateMask(feedElement);
  }

  unregisterFSS(feedElement: HTMLElement, id: string): void {
    const positions = this.fssPositions.get(feedElement);

    if (positions) {
      positions.delete(id);

      if (positions.size === 0) {
        // No more FSS ads, remove mask
        feedElement.style.removeProperty("--fss-mask");
      } else {
        this.updateMask(feedElement);
      }
    }
  }

  updateFSSPosition(feedElement: HTMLElement, position: FSSPosition): void {
    const positions = this.fssPositions.get(feedElement);

    if (positions && positions.has(position.id)) {
      positions.set(position.id, position);
      this.updateMask(feedElement);
    }
  }

  /**
   * Generate CSS mask using a single linear gradient with multiple color stops
   * Creates visible sections (black) with holes where FSS ads appear (transparent)
   */
  private generateMask(positions: Map<string, FSSPosition>): string {
    // Convert positions to sorted array
    const sortedPositions = Array.from(positions.values()).sort(
      (a, b) => a.top - b.top,
    );

    if (sortedPositions.length === 0) {
      return "none";
    }

    const stops: string[] = [];
    let lastBottom = 0;

    sortedPositions.forEach((pos) => {
      // Add visible section above this FSS ad
      if (pos.top > lastBottom) {
        stops.push(`black ${lastBottom}px`);
        stops.push(`black ${pos.top}px`);
      }

      // Add transparent section for the FSS ad (hole)
      stops.push(`transparent ${pos.top}px`);
      stops.push(`transparent ${pos.bottom}px`);

      lastBottom = Math.max(lastBottom, pos.bottom);
    });

    // Add final visible section after all FSS ads
    stops.push(`black ${lastBottom}px`);

    return `linear-gradient(to bottom, ${stops.join(", ")})`;
  }

  private updateMask(feedElement: HTMLElement): void {
    const positions = this.fssPositions.get(feedElement);

    if (!positions || positions.size === 0) {
      feedElement.style.removeProperty("--fss-mask");
      return;
    }

    const mask = this.generateMask(positions);
    feedElement.style.setProperty("--fss-mask", mask);

    console.log(`FSS: Updated mask with ${positions.size} hole(s)`);
  }

  // For debugging purposes
  getFSSPositions(feedElement: HTMLElement): FSSPosition[] {
    const positions = this.fssPositions.get(feedElement);
    return positions ? Array.from(positions.values()) : [];
  }
}

export const fssMaskManager = FSSMaskManager.getInstance();
export type { FSSPosition };
